import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { AuthService } from '../auth/auth.service';
import { environment } from 'src/environments/environment';

export type WatchTimeType = 'video' | 'audio';

/**
 * Accumulates the seconds of genuine playback (seeks already discarded by the
 * players) and posts them as deltas to media-api `v2/watch-time`, which adds
 * them to the customer's row for today in `customer_daily_watch_time`.
 *
 * Both players hand in sub-second deltas on every timeupdate; the service
 * batches them and only sends whole seconds — on pause / track change /
 * page leave, plus a timer so a long uninterrupted session is checkpointed
 * even if the tab is later killed.
 */
@Injectable({
  providedIn: 'root'
})
export class WatchTimeService {
  private pending:  { [type in WatchTimeType]: number }  = { video: 0, audio: 0 };
  private inFlight: { [type in WatchTimeType]: boolean } = { video: false, audio: false };
  private flushTimer: any = null;
  private readonly FLUSH_EVERY_MS = 60 * 1000;

  constructor(
    private apiService: ApiService,
    private authService: AuthService
  ) {}

  /** Called by a player for each chunk of genuine (non-seek) playback. */
  add(type: WatchTimeType, delta: number) {
    if (!delta || delta <= 0) { return; }
    this.pending[type] += delta;
    this.ensureTimer();
  }

  /**
   * Posts whatever whole seconds are pending. `keepalive` switches to a
   * `fetch({ keepalive: true })` so the request survives the page unloading —
   * HttpClient requests are cancelled by the browser at that point.
   */
  flush(type?: WatchTimeType, keepalive = false) {
    const types: WatchTimeType[] = type ? [type] : ['video', 'audio'];
    types.forEach(t => this.send(t, keepalive));
  }

  private send(type: WatchTimeType, keepalive: boolean) {
    if (!localStorage.getItem('currentUser')) {
      // Guest playback has no customer to attribute it to; drop it rather than
      // letting it leak into whoever logs in next.
      this.pending[type] = 0;
      return;
    }

    const seconds = Math.floor(this.pending[type]);
    if (seconds < 1) { return; }

    // The sub-second remainder stays pending so it is not lost once per report.
    this.pending[type] -= seconds;

    if (keepalive) {
      this.sendKeepalive(type, seconds);
      return;
    }

    if (this.inFlight[type]) {
      // Keep it for the next flush; the in-flight one will finish first.
      this.pending[type] += seconds;
      return;
    }

    this.inFlight[type] = true;
    this.apiService.callPostAPI('v2/watch-time', { type, seconds }, environment.apiUrl).subscribe(
      () => {
        this.inFlight[type] = false;
      },
      err => {
        this.inFlight[type] = false;
        // Network failure → retry with the next flush. A 4xx means the server
        // rejected it (bad token, validation) and resending would not help.
        if (!err || !err.status || err.status >= 500) {
          this.pending[type] += seconds;
        }
        console.warn('[WatchTimeService] watch-time report failed', err);
      }
    );
  }

  private sendKeepalive(type: WatchTimeType, seconds: number) {
    if (typeof fetch !== 'function') { return; }

    const auth = this.authService.decryption();
    const token = auth && auth.response && auth.response.access_token;
    if (!token) { return; }

    const accessKey = localStorage.getItem('selectedAccessFlag');
    const headers: { [key: string]: string } = {
      'Accept':               'application/json',
      'Content-Type':         'application/json',
      'Authorization':        'Bearer ' + token,
      'X-REQUEST-TYPE':       'web',
      'X-REQUEST-ACCESS-KEY': accessKey ? accessKey : 'kn',
      'X-LANGUAGE-CODE':      localStorage.getItem('currentLanguage') || 'en'
    };

    try {
      fetch(environment.apiUrl + 'v2/watch-time', {
        method: 'POST',
        headers,
        body: JSON.stringify({ type, seconds }),
        keepalive: true
      }).catch(() => { /* page is going away; nothing to retry into */ });
    } catch (e) {
      // Older browsers without keepalive support — the timer flush already
      // checkpointed everything but the last minute.
    }
  }

  private ensureTimer() {
    if (this.flushTimer) { return; }
    this.flushTimer = setInterval(() => {
      if (this.pending.video < 1 && this.pending.audio < 1) {
        clearInterval(this.flushTimer);
        this.flushTimer = null;
        return;
      }
      this.flush();
    }, this.FLUSH_EVERY_MS);
  }
}
